import { ReactComponent as DetailsIcon } from "assets/icons/details.svg";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import {
  DEFAULT_PAGE_SIZE_OPTION,
  handlePageClick,
  handlePageSizeChange,
} from "helpers/PaginationHelper";
import debounce from "lodash.debounce";
import {
  CustomTable,
  findUserRoleByLabel,
  Input,
  Modal,
  ModalHeader,
  OptionType,
  Pagination,
  RangeDatePicker,
  TableType,
  UserInfo,
  UserInfoLoading,
  useDateRangeClick,
  useTitle,
} from "modules/common";
import {
  ActivityFilters,
  getUserAccountData,
  getUserActivityData,
  getUserActivityRequest,
} from "modules/dashboard";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SortingRule } from "react-table";
import { RootState } from "store";

const PAGE_NEIGHBOURS = 1;
const INPUT_DEBOUNCE_TIME = 500;

export const UserActivity: React.FC = () => {
  useTitle("Aktivnost korisnika - eSavjetnik");

  const dispatch = useDispatch();

  const userActivity = useSelector((state: RootState) => state.userActivity);
  const userAccount = useSelector((state: RootState) => state.userAccount);
  const { userData } = userAccount;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [roleFilter, setRoleFilter] = useState<number[]>([]);
  const [platformFilter, setPlatformFilter] = useState<number[]>([]);
  const [actionFilter, setActionFilter] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortingRule<object>>();
  const [pageSize, setPageSize] = useState<OptionType>(DEFAULT_PAGE_SIZE_OPTION);
  const [currentPage, setCurrentPage] = useState(1);
  const [date, setDate] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date(),
  });

  const handleDayClick = useDateRangeClick({
      date,
      setDate,
      setCurrentPage,
      emptyValue: undefined,
  });

  const updateRange = (from: Date | undefined, to: Date | undefined) => {
    setDate({ from, to });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleModalOpen = useCallback(
    (uid: string, userRole: number) => {
      dispatch(getUserAccountData(uid, userRole));
      setIsModalOpen(true);
    },
    [dispatch]
  );

  const debouncedSearch = useCallback(
    debounce(
      (
        currentPage: number,
        searchTerm: string | null,
        size: number,
        roles: number[],
        platforms: number[],
        actions: number[],
        dateFrom: Date,
        dateTo: Date,
        sortBy: SortingRule<object> | undefined
      ) =>
        dispatch(
          getUserActivityData({
            pageNumber: currentPage,
            pageSize: size,
            userRoleFilter: [...roles],
            actionTypeFilter: [...actions],
            applicationTypeFilter: [...platforms],
            search: searchTerm,
            dateFrom,
            dateTo,
            userUid: null,
            sortField: sortBy?.id,
            descending: sortBy?.desc,
          })
        ),
      INPUT_DEBOUNCE_TIME
    ),
    [dispatch]
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (!date.from || !date.to) {
        return;
    }
    const size = parseInt(pageSize.value);
    debouncedSearch(
      currentPage,
      searchTerm,
      size,
      roleFilter,
      platformFilter,
      actionFilter,
      date.from as Date,
      date.to as Date,
      sortBy
    );
  };

  useEffect(() => {
    if (!date.from || !date.to) {
        return;
    }

    const size = parseInt(pageSize.value);
    dispatch(getUserActivityRequest());
    debouncedSearch(
      currentPage,
      searchTerm,
      size,
      roleFilter,
      platformFilter,
      actionFilter,
      date.from as Date,
      date.to as Date,
      sortBy
    );
  }, [
    currentPage,
    date.from,
    date.to,
    debouncedSearch,
    dispatch,
    pageSize.value,
    roleFilter,
    platformFilter,
    actionFilter,
    searchTerm,
    sortBy,
  ]);

  const tableColumns = useMemo(
    () => [
      {
        Header: "PODACI O KORISNIKU",
        id: "user",
        accessor: (data: any) => (
          <div>
            <span>
              {data.firstName} {data.lastName}
            </span>
          </div>
        ),
      },
      {
        Header: "DATUM I VRIJEME",
        id: "date",
        accessor: (data: any) => (
          <span className="date-time">{getDisplayDateFormat(data.createdAt, true)}</span>
        ),
        className: "date-time-header",
      },
      {
        Header: "APLIKACIJA",
        accessor: "application",
        className: "application-header",
      },
      {
        Header: "ULOGA",
        accessor: "userRole",
        className: "role-header",
      },
      {
        Header: "AKCIJA",
        id: "action",
        accessor: (data: any) => <span className="user-action">{data.userAction}</span>,
      },
      {
        Header: "KREIRAO",
        accessor: "createdByName",
        className: "created-by-header",
      },
      {
        id: "info",
        disableSortBy: true,
        className: "administration-table-header actions-cell",
        Cell: ({ row }: any) => {
          const { original } = row;
          const type = findUserRoleByLabel(original.userRole);
          return (
            <DetailsIcon
              className="details-icon"
              onClick={() => handleModalOpen(original.userUid, type?.value ?? 0)}
            />
          );
        },
      },
    ],
    [handleModalOpen]
  );

  return (
    <>
      <div className="user-activity">
        <div className="f-c g-24 even">
          <div className="f-r g-24 even">
            <ActivityFilters
              roleFilter={roleFilter}
              platformFilter={platformFilter}
              actionFilter={actionFilter}
              setRoleFilter={setRoleFilter}
              setPlatformFilter={setPlatformFilter}
              setActionFilter={setActionFilter}
              setCurrentPage={setCurrentPage}
              searchFilter={searchTerm}
              setSearchFilter={setSearchTerm}
            />

            <div className="f-r g-24 even">
              <RangeDatePicker
                onClick={handleDayClick}
                selectedDays={date}
                modifiers={{ start: date.from, end: date.to }}
                updateRange={updateRange}
                disableFutureDays={true}
              />

              <Input
                name="user-activity-search"
                className="user-activity-search"
                id="user-activity-search"
                type="text"
                size="medium"
                placeholder="Pretraži korisnike."
                value={searchTerm}
                onChange={handleSearchChange}
                leftIcon={<SearchIcon className="search-icon" />}
              />
            </div>
          </div>

          <div className="user-activity-table">
            <CustomTable
              data={userActivity.data ?? []}
              columns={tableColumns}
              setSortBy={setSortBy}
              tableType={TableType.AdministrationTable}
              loading={userActivity.isLoading}
              flex
            />

            {Boolean(
              !userActivity.isLoading && userActivity.data && userActivity.data.length
            ) && (
              <Pagination
                currentPage={currentPage}
                numberOfPages={Math.ceil(
                  userActivity.userActivityLogCount / parseInt(pageSize.value)
                )}
                pageNeighbours={PAGE_NEIGHBOURS}
                pageSize={pageSize}
                onPageSizeChange={(v) =>
                  handlePageSizeChange(v, setPageSize, setCurrentPage)
                }
                handlePageClick={(e) => handlePageClick(e, setCurrentPage)}
                handlePageSelect={(e) => setCurrentPage(e)}
                next={() => setCurrentPage((p) => p + 1)}
                prev={() => setCurrentPage((p) => p - 1)}
                first={() => setCurrentPage(1)}
                last={() =>
                  setCurrentPage(
                    Math.ceil(
                      userActivity.userActivityLogCount /
                        parseInt(pageSize?.value ?? DEFAULT_PAGE_SIZE_OPTION.value)
                    )
                  )
                }
              />
            )}
          </div>
        </div>
      </div>

      <Modal
        isActive={isModalOpen}
        onClose={handleModalClose}
        modalHeader={
          <ModalHeader
            onClose={handleModalClose}
            title={`${userData?.firstName ?? ""} ${userData?.lastName ?? ""}`}
            isLoading={userAccount.isFetchingUserData || !userData}
          />
        }
      >
        {userAccount.isFetchingUserData || !userData ? (
          <UserInfoLoading />
        ) : (
          <UserInfo userInfo={userData} showActivity={true} />
        )}
      </Modal>
    </>
  );
};
